import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ContentSettingsFormValues {
    mission: string;
    description: string;
}

const ContentSettings = () => {
    const { accountId, mission, description } = useCurrentAccount();
    const toast = useToast();

    const methods = Form.useGivebutterForm<ContentSettingsFormValues>({
        defaultValues: {
            mission: mission || '',
            description: description || '',
        },
    });
    const { control, handleSubmit, path, setError, resetFormButton } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        ContentSettingsFormValues
    >({
        mutationFn: values => updateAccount(accountId, values),
        onSuccess: () => {
            toast.success('Updated Content settings!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update Content settings');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof ContentSettingsFormValues, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Content</SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col gap-4"
                    >
                        <Form.Container>
                            <Form.Label className="text-base" name={path('mission')}>
                                Mission Statement
                            </Form.Label>
                            <Form.Textarea
                                control={control}
                                name="mission"
                                rows={8}
                                placeholder="Optional"
                            />
                            <Form.Error name={path('mission')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="text-base" name={path('description')}>
                                About your Organization
                            </Form.Label>
                            <Wysiwyg.Root>
                                <UseFormWysiwyg
                                    name={path('description')}
                                    control={control}
                                    inline={false}
                                    init={{
                                        statusbar: false,
                                        placeholder: 'Tell your story...',
                                        plugins: Wysiwyg.Constants.MINI_EDITOR_PLUGINS,
                                        toolbar: Wysiwyg.Constants.MINI_EDITOR_TOOLBAR,
                                        content_style: Wysiwyg.Constants.APP_FONT,
                                        menubar: false,
                                    }}
                                />
                            </Wysiwyg.Root>
                            <Form.Error name={path('description')} />
                        </Form.Container>

                        <Form.SubmitButton className="self-start" isSubmitting={isLoading}>
                            Save
                        </Form.SubmitButton>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { ContentSettings };
